/** Composant racine - routes et garde d'authentification */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { authApi } from './services/api';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ProfilePage } from './pages/ProfilePage';
import { DashboardPage } from './pages/DashboardPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { EmployeeDetailPage } from './pages/EmployeeDetailPage';
import { BadgeEnrollPage } from './pages/BadgeEnrollPage';
import { TimeLogsPage } from './pages/TimeLogsPage';
import { ReadersPage } from './pages/ReadersPage';
import { SchedulesPage } from './pages/SchedulesPage';
import { AssignmentPage } from './pages/AssignmentPage';
import { SitesPage } from './pages/SitesPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { RoundsPage } from './pages/RoundsPage';
import { CleaningZonesPage } from './pages/CleaningZonesPage';
import { EpisPage } from './pages/EpisPage';
import { PointingControlPage } from './pages/PointingControlPage';
import { UsersPage } from './pages/UsersPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Redirige vers /login si non authentifié */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Accueil : tableau de bord pour les admins, affectation pour les chefs d'équipe */
function HomeRoute() {
  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => authApi.me(),
    staleTime: 60_000,
  });
  if (isLoading) return null;
  if (me?.role === 'manager') return <Navigate to="/assignment" replace />;
  return <DashboardPage />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route path="/" element={<HomeRoute />} />
              <Route path="/employees" element={<EmployeesPage />} />
              <Route path="/employees/:id" element={<EmployeeDetailPage />} />
              <Route path="/badge-enroll" element={<BadgeEnrollPage />} />
              <Route path="/time-logs" element={<TimeLogsPage />} />
              <Route path="/sites" element={<SitesPage />} />
              <Route path="/readers" element={<ReadersPage />} />
              <Route path="/assignment" element={<AssignmentPage />} />
              <Route path="/schedules/boulangerie" element={<SchedulesPage activity="boulangerie" />} />
              <Route path="/schedules/traiteur" element={<SchedulesPage activity="traiteur" />} />
              <Route path="/schedules/chambre-froide" element={<SchedulesPage activity="chambre_froide" />} />
              <Route path="/schedules/cleaning" element={<SchedulesPage activity="cleaning" />} />
              <Route path="/schedules/office" element={<SchedulesPage activity="office" />} />
              {/* Les chauffeurs n'ont plus de planning type : leur tournée se choisit
                  jour par jour dans Affectation. Redirection pour les liens existants. */}
              <Route path="/schedules/driving" element={<Navigate to="/assignment" replace />} />
              <Route path="/vehicles" element={<VehiclesPage />} />
              <Route path="/rounds" element={<RoundsPage />} />
              <Route path="/cleaning-zones" element={<CleaningZonesPage />} />
              <Route path="/epis" element={<EpisPage />} />
              <Route path="/pointing-control" element={<PointingControlPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/profile" element={<ProfilePage />} />
            </Route>
            {/* URL inconnue : retour à l'accueil plutôt qu'une page blanche */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
